import { type ColumnDef } from "@tanstack/react-table";
import { type Api, removeWorkout as removeWorkoutApi } from "api-client";
import { useTranslation } from "react-i18next";

import {
  DataTable,
  prebuiltActions,
  type RowActionInput,
} from "src/components/DataTable";
import { type DataTableFeatures } from "src/components/dataTableFeatures";
import { SectionDetails } from "src/components/SectionDetails";
import { useTableSearch } from "src/components/TableSearchContext";

import { SectionDialogEdit } from "./SectionDialog";

type Section = Api.ListSections.Output.Self["currentPage"][number];

export { SectionDetails } from "src/components/SectionDetails";

interface Props {
  sectionList: Api.ListSections.Output.Self;
  selectedSectionId: string | null;
  toolbarTrailing?: React.ReactNode;
  onSelectedSectionChange: (sectionId: string) => void;
  onSelectedSectionClose: () => void;
  onMutated: () => void;
}

export function SectionsTable(props: Props) {
  const { t } = useTranslation();
  const tableSearch = useTableSearch();
  const items = props.sectionList.currentPage;
  const pageInfo = props.sectionList.pageInfo;

  const columns: ColumnDef<DataTableFeatures, Section>[] = [
    {
      id: "name",
      header: t("sectionsPage.name"),
      cell: ({ row }) => {
        return <div className="text-left font-medium">{row.original.name}</div>;
      },
    },
    {
      id: "details",
      header: t("sectionsPage.details"),
      cell: ({ row }) => {
        return <SectionDetails section={row.original} />;
      },
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={items}
        getRowId={(row) => {
          return row.id;
        }}
        onRowClick={(row) => {
          props.onSelectedSectionChange(row.original.id);
        }}
        searchPlaceholder={t("sectionsPage.searchPlaceholder")}
        globalFilter={tableSearch.q}
        onGlobalFilterChange={tableSearch.setSearch}
        manualFiltering
        manualPagination
        rowCount={pageInfo.count}
        pagination={tableSearch.pagination}
        onPaginationChange={tableSearch.onPaginationChange}
        toolbarTrailing={props.toolbarTrailing}
        emptyState={{
          icon: "tabler-section",
          title: t("sectionsPage.emptyTitle"),
          description: t("sectionsPage.emptyDescription"),
        }}
        rowActions={(row): RowActionInput[] => {
          return [
            {
              ...prebuiltActions.delete,
              mutationFn: async () => {
                await removeWorkoutApi({
                  path: { workoutId: row.original.id },
                });
              },
              onSuccess: () => {
                props.onMutated();
              },
              dialog: {
                destructive: true,
                title: t("sectionsPage.delete.title"),
                description: t("sectionsPage.delete.description", {
                  name: row.original.name,
                }),
              },
            },
          ];
        }}
      />
      {props.selectedSectionId && (
        <SectionDialogEdit
          sectionId={props.selectedSectionId}
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              props.onSelectedSectionClose();
              props.onMutated();
            }
          }}
        />
      )}
    </>
  );
}
